import type { StoreApi } from 'zustand/vanilla';

import { createStore } from 'zustand/vanilla';

import type { SpecState } from '../qualityFormulaTypes';
import type { RunPreviewSlice } from './slices/runPreviewSlice';
import type { CreateSpecRowEditingSliceOptions, SpecRowEditingSlice } from './slices/specRowEditingSlice';
import type { CreateSpecVisibilitySliceOptions, SpecVisibilitySlice } from './slices/specVisibilitySlice';
import type { TestValuesSlice } from './slices/testValuesSlice';

import { showSuccessNotification } from '../../../utils/notifications';
import { createRunPreviewSlice } from './slices/runPreviewSlice';
import { createSpecRowEditingSlice } from './slices/specRowEditingSlice';
import { createSpecVisibilitySlice } from './slices/specVisibilitySlice';
import { createTestValuesSlice } from './slices/testValuesSlice';

export interface QualityFormulaStoreActions {
  /** Clears every enabled spec's entered values, every test value, and any Step 3 results — the "Reset" button. */
  handleResetInputs: () => void;
}

export type QualityFormulaStore = QualityFormulaStoreActions &
  RunPreviewSlice &
  SpecRowEditingSlice &
  SpecVisibilitySlice &
  TestValuesSlice;

export type CreateQualityFormulaStoreOptions = CreateSpecRowEditingSliceOptions & CreateSpecVisibilitySliceOptions;

export type { SpecState };

/**
 * Creates one workspace's worth of quality-formula state (Step 1's per-spec formula config, Step
 * 2's sample test values, Step 3's preview results) as a standalone Zustand store rather than a
 * singleton — Contract Entry's playground and the Template Editor each mount their own instance
 * (via `QualityFormulaStoreProvider`), seeded from `options`, so editing one never leaks into the
 * other. Every component under that provider reads/writes this store directly through
 * `useQualityFormulaStore` selectors instead of the previous prop-drilled handler chain.
 *
 * Composed from four focused slices (see `./slices`) rather than one flat set of state/actions —
 * `specRowEditingSlice` and `specVisibilitySlice` split Step 1's own two concerns (editing an
 * already-visible spec's formula vs. which specs are visible/expanded at all), alongside Step 2's
 * `testValuesSlice` and Step 3's `runPreviewSlice`. Combining them into one Zustand store (rather
 * than four separate ones) is what lets actions like `handleVisibleSpecTypesChange` and
 * `runPreview` reach across slices via `get()` without any slice importing another.
 */
export const createQualityFormulaStore = (
  options: CreateQualityFormulaStoreOptions = {},
): StoreApi<QualityFormulaStore> =>
  createStore<QualityFormulaStore>()((...args) => ({
    ...createSpecRowEditingSlice(options)(...args),
    ...createSpecVisibilitySlice(options)(...args),
    ...createTestValuesSlice(...args),
    ...createRunPreviewSlice(...args),

    handleResetInputs: () => {
      const [, get] = args;
      get().resetSpecValues();
      get().resetTestValues();
      get().handleClearResults();
      showSuccessNotification('Entered values have been reset.');
    },
  }));
